import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, Image, LayoutGrid, Newspaper, Palette, User, X, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { AccountTab } from './AccountTab';
import { AppearanceTab } from './AppearanceTab';
import { CalendarTab, NewsTab } from './FeedsTabs';
import { MediaTab } from './MediaTab';
import { WidgetsTab } from './WidgetsTab';

const TABS: Array<{ id: string; name: string; icon: LucideIcon; render: () => ReactElement }> = [
  { id: 'appearance', name: 'Appearance', icon: Palette, render: () => <AppearanceTab /> },
  { id: 'media', name: 'Media', icon: Image, render: () => <MediaTab /> },
  { id: 'widgets', name: 'Widgets', icon: LayoutGrid, render: () => <WidgetsTab /> },
  { id: 'calendar', name: 'Calendar', icon: CalendarDays, render: () => <CalendarTab /> },
  { id: 'news', name: 'News', icon: Newspaper, render: () => <NewsTab /> },
  { id: 'account', name: 'Account', icon: User, render: () => <AccountTab /> },
];

export function SettingsPanel({ open, onClose, initialTab }: { open: boolean; onClose: () => void; initialTab?: string }) {
  const [tab, setTab] = useState(initialTab ?? 'appearance');
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && initialTab) setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the panel for remote/keyboard users.
    window.setTimeout(() => panel.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus(), 50);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl bg-[var(--surface-strong)] text-[var(--fg)] shadow-2xl backdrop-blur-xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <nav className="flex w-16 shrink-0 flex-col gap-1 border-r border-[var(--border)] p-2 sm:w-44" role="tablist" aria-orientation="vertical">
              <div className="mb-3 hidden px-2 pt-2 text-sm font-semibold sm:block">KCL Canvas</div>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={t.id === active.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition ${
                    t.id === active.id ? 'bg-[var(--accent)] text-black' : 'hover:bg-[var(--border)]'
                  }`}
                >
                  <t.icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">{t.name}</span>
                </button>
              ))}
              <div className="muted mt-auto hidden space-y-1 px-2 pb-2 text-xs sm:block">
                <a href="/privacy" className="block hover:underline">
                  Privacy
                </a>
                <a href="/terms" className="block hover:underline">
                  Terms
                </a>
                <a href="https://kcltd.es" target="_blank" rel="noopener" className="block hover:underline">
                  by Kirkpatrick Consult Limited
                </a>
              </div>
            </nav>
            <div className="min-w-0 flex-1 overflow-y-auto p-6" role="tabpanel">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold">{active.name}</h2>
                <button onClick={onClose} className="rounded-full p-2 hover:bg-[var(--border)]" aria-label="Close settings">
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              {active.render()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
